import { SEC_ApiCall, SEC_DataApiPost } from '../func';

/** Loads the current user's permitted form summaries. */
export async function getAvailableForms() {
    const response = await SEC_ApiCall('forms', {});
    if (!response || typeof response !== 'object' || !Array.isArray(response.forms)) {
        throw new Error(response?.message || 'Unexpected Forms response.');
    }
    return response.forms;
}

/** Loads one complete renderable form after the API rechecks user access. */
export async function getFormDefinition(formId) {
    const response = await SEC_ApiCall('forms', { formId });
    if (!response || typeof response !== 'object' || !response.form) {
        throw new Error(response?.message || 'Unexpected form response.');
    }
    return response.form;
}

/** Validates and persists one completed custom form. */
export async function createFormSubmission(payload) {
    const response = await SEC_DataApiPost(
        'forms/submissions',
        JSON.stringify(payload),
        {}
    );
    if (!response || typeof response !== 'object' || response.submitted !== true) {
        throw new Error(response?.message || 'Unexpected form submission response.');
    }
    return response;
}

/** Loads workflow stages currently assigned to the authenticated user. */
export async function getActiveFormSteps() {
    const response = await SEC_ApiCall('forms/steps', {});
    if (!response || !Array.isArray(response.steps)) throw new Error(response?.message || 'Unexpected workflow response.');
    return response.steps;
}

/** Completes the current assigned stage and advances its workflow. */
export async function completeFormStep(payload) {
    const response = await SEC_DataApiPost('forms/responses/steps/complete', JSON.stringify(payload), {});
    if (!response || response.completed !== true) throw new Error(response?.message || 'Unexpected workflow update.');
    return response;
}

/** Reopens a completed workflow stage as a form administrator. */
export async function reopenFormStep(payload) {
    const response = await SEC_DataApiPost('forms/responses/steps/reopen', JSON.stringify(payload), {});
    if (!response || response.reopened !== true) throw new Error(response?.message || 'Unexpected workflow reopen response.');
    return response;
}

/** Reassigns the current workflow stage as a form administrator. */
export async function reassignFormStep(payload) {
    const response = await SEC_DataApiPost('forms/responses/steps/reassign', JSON.stringify(payload), {});
    if (!response || response.reassigned !== true) throw new Error(response?.message || 'Unexpected workflow reassignment response.');
    return response;
}

/** Loads response summaries for a form administered by the current user. */
export async function getFormResponses(formId, includeArchived = false) {
    const response = await SEC_ApiCall('forms/responses', {
        formId,
        includeArchived: includeArchived ? 'true' : 'false',
    });
    if (!response || typeof response !== 'object' || !Array.isArray(response.responses)) {
        throw new Error(response?.message || 'Unexpected form responses response.');
    }
    return response.responses;
}

/** Loads one server-paginated page of responses submitted by the current user. */
export async function getMyFormResponses(cursor = '', formId = '') {
    const params = { scope: 'mine' };
    if (cursor) params.cursor = cursor;
    if (formId) params.formId = formId;
    const response = await SEC_ApiCall('forms/responses', params);
    if (!response || typeof response !== 'object' || !Array.isArray(response.responses)) {
        throw new Error(response?.message || 'Unexpected submitted forms response.');
    }
    return {
        responses: response.responses,
        nextCursor: String(response.nextCursor || ''),
    };
}

/** Loads one complete response after the API rechecks form administration access. */
export async function getFormResponse(formId, submissionId, submittedAt) {
    const response = await SEC_ApiCall('forms/responses/detail', { formId, submissionId, submittedAt });
    if (!response || typeof response !== 'object' || !response.response) {
        throw new Error(response?.message || 'Unexpected form response detail.');
    }
    return response.response;
}

/** Marks one administered response as actioned and stores the administrator's choices. */
export async function updateFormResponseActioned(formId, submissionId, submittedAt, actionedData) {
    const response = await SEC_DataApiPost('forms/responses/actioned', JSON.stringify({
        formId,
        submissionId,
        submittedAt,
        actionedData,
    }), {});
    if (!response || typeof response !== 'object' || response.actioned !== true) {
        throw new Error(response?.message || 'Unexpected form response update.');
    }
    return response;
}

/** Archives one administered response and moves it out of the live counters. */
export async function archiveFormResponse(formId, submissionId, submittedAt) {
    const response = await SEC_DataApiPost('forms/responses/archive', JSON.stringify({
        formId,
        submissionId,
        submittedAt,
    }), {});
    if (!response || typeof response !== 'object' || response.isArchived !== true) {
        throw new Error(response?.message || 'Unexpected form response archive result.');
    }
    return response;
}
